import { useCallback, useEffect } from 'react';
import {
  NavigateFunction,
  NavigateOptions,
  To,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { isEmbeddedInNativeShell } from 'app/utils/webviewAuth';

type PendingArrival = {
  from: number;
  idx: number;
  to?: To;
  options?: NavigateOptions;
  at: number;
};

const DOUBLE_TAP_MS = 1000;

let pendingArrival: PendingArrival | null = null;

const historyDepth = (): number => window.history.state?.idx ?? 0;

export const popThenReplace = (
  navigate: NavigateFunction,
  steps: number,
  to?: To,
  options?: NavigateOptions
) => {
  const depth = historyDepth();
  if (depth === 0) {
    navigate(to ?? window.location.pathname + window.location.search, {
      ...options,
      replace: true,
    });
    return;
  }
  if (pendingArrival && Date.now() - pendingArrival.at < DOUBLE_TAP_MS) return;
  const count = Math.min(steps, depth);
  pendingArrival = {
    from: depth,
    idx: depth - count,
    to,
    options,
    at: Date.now(),
  };
  navigate(-count);
};

export const useShellArrival = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pending = pendingArrival;
    if (!pending || historyDepth() === pending.from) return;
    pendingArrival = null;
    if (historyDepth() !== pending.idx) return;
    navigate(pending.to ?? location.pathname + location.search, {
      ...pending.options,
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
};

export const useShellHistory = () => {
  const navigate = useNavigate();
  const isEmbedded = isEmbeddedInNativeShell();
  const canPop = isEmbedded && historyDepth() > 0;

  const back = useCallback(
    (fallback: () => void) => (canPop ? navigate(-1) : fallback()),
    [canPop, navigate]
  );

  const backTo = useCallback(
    (to: To, options?: NavigateOptions, steps = 1) => {
      if (isEmbedded) popThenReplace(navigate, steps, to, options);
      else navigate(to, options);
    },
    [isEmbedded, navigate]
  );

  return { replace: isEmbedded, back, backTo };
};
